import { Avatar, Box, Drawer, Grid, Typography } from "@mui/material";
import { useMember } from "api/members";
import { useAppSelector } from "app/hooks";
import { BoxSection } from "components/BoxSection/BoxSection";
import { CopyWithAlert } from "components/CopyWithAlert/CopyWithAlert";
import { ReactComponent as ChevronRightIcon } from "components/Icons/chevron-right.svg";
import { ReactComponent as Expand04Icon } from "components/Icons/expand-04.svg";
import { ReactComponent as Trophy01Icon } from "components/Icons/trophy-01.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { StatusValue } from "components/Table/StatusValue";
import { OLTabs } from "components/Tabs/OLTabs";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import useCopyWithAlert from "hooks/useCopyWithAlert";
import { useIsOverflown } from "hooks/useIsOverflown";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { Fragment, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { alphabetizeByProperty } from "utilities/arrays";
import { links } from "utilities/links";
import { formatCurrencyValue, formatNumberValue } from "utilities/numbers";
import { getAcronym, pickPluralizedUnit } from "utilities/strings";
import {
  Item,
  More,
  SegmentItem,
} from "../../SingleMember/SegmentsAndCustomAttributes";
import { useStyles } from "./MemberDetailsDrawer.styles";

const MemberDetailsSection = ({
  item,
  isElementLast,
}: {
  item: {
    copy?: boolean;
    label: string;
    value?: string | JSX.Element;
  };
  isElementLast: boolean;
}) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { alertVisibility, handleCopy } = useCopyWithAlert();
  const { observeElement, isOverflown } = useIsOverflown();

  return (
    <>
      <Grid
        item
        xs={4}
        mb={isElementLast ? 0 : 12}
        className={classes.subtitle}
      >
        {message({ id: item.label })}
      </Grid>
      <Grid
        item
        xs={8}
        mb={isElementLast ? 0 : 12}
        className={`${classes.detailsValue} ${item.copy ? classes.clickable : ""}`}
        onClick={() =>
          item.copy && typeof item.value === "string" && handleCopy(item.value)
        }
        data-hj-suppress={true}
      >
        <Tooltip disableHoverListener={!isOverflown} text={item.value}>
          <div ref={observeElement} className={classes.ellipsis}>
            {item.value || "-"}
          </div>
        </Tooltip>
        <CopyWithAlert
          className={classes.copyInfo}
          alertVisibility={alertVisibility}
          canCopy={!!item.copy && !!item.value}
          value={typeof item.value === "string" ? item.value : undefined}
        />
      </Grid>
    </>
  );
};

export function MemberDetailsDrawer({
  memberId,
  open,
  onClose,
}: {
  memberId: string;
  open: boolean;
  onClose: () => void;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { useMembersData } = useMember(memberId);
  const { data: membersData } = useMembersData();
  const { observeElement, isOverflown } = useIsOverflown();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { useMemberWalletsData } = useMember(memberId);
  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const { data: memberWalletsData } = useMemberWalletsData({
    rows: 50,
  });
  const defaultWalletId = memberWalletsData?.items.find(
    ({ walletType }) => walletType.code === defaultWallet.code,
  )?.walletId;
  const [selectedWalletId, setSelectedWalletId] = useState<false | string>(
    false,
  );
  const currentWallet = memberWalletsData?.items.find(
    (wallet) => wallet.walletId === selectedWalletId,
  );

  const visibleItems = 2;
  const isMemberAnonymized = membersData?.anonymized;
  const walletType = currentWallet?.walletType;
  const walletAccount = currentWallet?.account;
  const digitalWalletItems: {
    id: string;
    label: string;
  }[] = useMemo(
    () =>
      alphabetizeByProperty(
        memberWalletsData?.items?.map(({ walletId, walletType }) => ({
          id: walletId,
          label: walletType.name,
        })),
        "label",
      ),
    [memberWalletsData],
  );
  useEffect(() => {
    if (defaultWalletId) {
      setSelectedWalletId(defaultWalletId);
    }
  }, [memberWalletsData, defaultWalletId]);

  const memberDetails = membersData
    ? [
        {
          copy: true,
          label: message({ id: "memberID" }),
          value: membersData.customerId,
        },
        {
          copy: true,
          label: message({ id: "email" }),
          value: membersData.email,
        },
        {
          copy: !!membersData.phone,
          label: message({ id: "phoneNumber" }),
          value: membersData.phone,
        },
        {
          copy: !!membersData.loyaltyCardNumber,
          label: message({ id: "loyaltyCardNumber" }),
          value: membersData.loyaltyCardNumber,
        },
        {
          label: message({ id: "segments" }),
          value: membersData ? (
            <div className={classes.itemsContainer}>
              {membersData.segments.length ? (
                <>
                  {membersData.segments
                    ?.slice(0, visibleItems)
                    .map((item: string) => (
                      <SegmentItem
                        className={classes.item}
                        id={item}
                        key={item}
                      />
                    ))}
                  {membersData.segments.length > visibleItems && (
                    <More
                      onClick={() =>
                        navigate(links.singleMemberGeneralInfo(memberId))
                      }
                      value={membersData.segments.length - visibleItems}
                    />
                  )}
                </>
              ) : (
                "-"
              )}
            </div>
          ) : (
            <Skeleton height={100} />
          ),
        },
        {
          label: message({ id: "customAttributes" }),
          value: membersData ? (
            <div className={classes.itemsContainer}>
              {membersData.labels.length ? (
                <>
                  {membersData.labels
                    ?.slice(0, visibleItems)
                    .map((item) => (
                      <Item
                        className={classes.item}
                        key={item.key}
                        value={`${item.key}: ${item.value}`}
                      />
                    ))}
                  {membersData.labels.length > visibleItems && (
                    <More
                      onClick={() =>
                        navigate(links.singleMemberGeneralInfo(memberId))
                      }
                      value={membersData.labels.length - visibleItems}
                    />
                  )}
                </>
              ) : (
                "-"
              )}
            </div>
          ) : (
            <Skeleton height={100} />
          ),
        },
      ]
    : [];

  return (
    <Drawer
      anchor="right"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
    >
      <div className={classes.header}>
        <div
          className={classes.iconWrapper}
          onClick={() => navigate(links.singleMemberGeneralInfo(memberId))}
        >
          <Expand04Icon className={classes.expandIcon} />
        </div>
        <Typography className={classes.title}>
          {message({ id: "memberDetails" })}
        </Typography>
        <XCloseIcon onClick={onClose} className={classes.closeIcon} />
      </div>
      <div className={classes.content}>
        <Grid container className={classes.detailsBox}>
          <div className={classes.mainDataContainer}>
            {membersData ? (
              <Avatar
                alt={`${membersData.firstName} ${membersData.lastName}`}
                className={classes.avatar}
                variant="square"
                data-hj-suppress
              >
                {getAcronym(
                  isMemberAnonymized
                    ? message({ id: "undefined" })
                    : `${membersData.firstName} ${membersData.lastName}`,
                )}
              </Avatar>
            ) : (
              <Skeleton height={48} width={49} />
            )}
            <div className={classes.hiddenOverflow}>
              <div className={classes.headerContainer}>
                {membersData ? (
                  isMemberAnonymized ? (
                    <div className={classes.userHeader}>
                      {message({ id: "undefined" })}
                    </div>
                  ) : membersData.firstName || membersData.lastName ? (
                    <div
                      className={classes.userHeader}
                      data-hj-suppress
                      ref={observeElement}
                    >
                      <Tooltip
                        text={`${membersData?.firstName} ${membersData?.lastName}`}
                        disableHoverListener={!isOverflown}
                        placement="bottom-start"
                      >
                        <span>
                          {membersData.firstName} {membersData.lastName}
                        </span>
                      </Tooltip>
                    </div>
                  ) : null
                ) : (
                  <Skeleton width={100} />
                )}
                <StatusValue status={membersData?.active} />
                {membersData?.currentLevel?.name && (
                  <StatusValue
                    status="neutral"
                    label={
                      <>
                        <Trophy01Icon />
                        {membersData.currentLevel.name}
                      </>
                    }
                  />
                )}
              </div>
              <div
                className={`${classes.smallText} ${classes.ellipsis}`}
                data-hj-suppress
              >
                {membersData ? membersData.email : <Skeleton width={100} />}
              </div>
            </div>
          </div>
        </Grid>
        <Grid container className={classes.detailsBox} mt={4}>
          {memberDetails.map((item, index) => (
            <Fragment key={index}>
              <MemberDetailsSection
                item={item}
                isElementLast={index + 1 === memberDetails.length}
              />
            </Fragment>
          ))}
        </Grid>
        <Box className={classes.titleSection}>
          <Typography m={0}>
            {message({ id: "businessPerformance" }).toUpperCase()}
          </Typography>
          <OLButton
            endIcon={<ChevronRightIcon />}
            onClick={() => navigate(links.singleMemberGeneralInfo(memberId))}
          >
            {message({ id: "viewAll" }).toLocaleUpperCase()}
          </OLButton>
        </Box>
        <OLPaper noPadding>
          <BoxSection
            items={[
              {
                label: message({ id: "totalSpending" }),
                value: formatCurrencyValue(
                  membersData?.transactionsAmount,
                  "-",
                  currency,
                ),
              },
              {
                label: message({ id: "purchaseTransactions" }),
                value: membersData?.transactionsCount || "-",
              },
              {
                label: message({ id: "avgTransactionValue" }),
                value: formatCurrencyValue(
                  membersData?.averageTransactionAmount,
                  "-",
                  currency,
                ),
              },
              {
                label: message({ id: "totalReturns" }),
                value: formatCurrencyValue(
                  membersData?.returnTransactionsAmount,
                  "-",
                  currency,
                ),
              },
              {
                label: message({ id: "returnTransactions" }),
                value: membersData?.returnTransactionsCount || "-",
              },
              {
                label: message({ id: "avgReturnValue" }),
                value: formatCurrencyValue(
                  membersData?.averageTransactionAmount,
                  "-",
                  currency,
                ),
              },
            ]}
            rowsPerPage={3}
          />
        </OLPaper>
        <Box className={classes.titleSection}>
          <Typography>
            {digitalWalletItems?.length === 1
              ? digitalWalletItems?.[0]?.label
              : message({ id: "wallets" }).toUpperCase()}
          </Typography>
          <OLButton
            endIcon={<ChevronRightIcon />}
            onClick={() => navigate(links.singleMemberWallets(memberId))}
          >
            {message({ id: "viewAll" }).toLocaleUpperCase()}
          </OLButton>
        </Box>
        {!!digitalWalletItems && digitalWalletItems?.length > 1 && (
          <OLTabs
            className={classes.tiersHeaderWrapper}
            items={digitalWalletItems?.map((item) => {
              return {
                name: item.label,
                id: item.id,
              };
            })}
            maxLabelLength={30}
            onChange={(_, tab: string) => {
              setSelectedWalletId(tab);
            }}
            value={selectedWalletId}
          />
        )}
        <OLPaper noPadding>
          {selectedWalletId ? (
            <>
              <div className={classes.walletDetails}>
                <div className={classes.walletName}>
                  {walletType?.name} ({walletType?.unitPluralName})
                  <StatusValue status={!!walletType?.active} />
                </div>
                <Typography className={classes.walletActivePoints}>
                  {formatNumberValue(walletAccount?.activeUnits)}
                  <span>
                    {message(
                      { id: "activeUnitSingularOrPlural" },
                      {
                        units: pickPluralizedUnit(
                          walletAccount?.activeUnits as number,
                          walletType?.unitSingularName,
                          walletType?.unitPluralName,
                        ),
                      },
                    )}
                  </span>
                </Typography>
              </div>
              <div className={classes.divider} />
              <BoxSection
                items={[
                  {
                    label: "pending",
                    value: formatNumberValue(walletAccount?.lockedUnits),
                  },
                  {
                    label: "expired",
                    value: formatNumberValue(walletAccount?.expiredUnits),
                  },
                  {
                    label: "spent",
                    value: formatNumberValue(walletAccount?.spentUnits),
                  },
                ]}
                rowsPerPage={3}
              />
            </>
          ) : (
            <Skeleton height={100} />
          )}
        </OLPaper>
      </div>
      <Box className={classes.btnContainer}>
        <OLButton onClick={onClose} variant="outlined">
          {message({ id: "close" })}
        </OLButton>
        <OLButton
          className={classes.saveButton}
          onClick={() => navigate(links.singleMemberGeneralInfo(memberId))}
          type="submit"
          variant="contained"
        >
          {message({ id: "seeFullProfile" })}
        </OLButton>
      </Box>
    </Drawer>
  );
}
